import { 
  Task, 
  TaskFilters, 
  TaskGrouping, 
  TaskListResponse, 
  CreateTaskRequest, 
  UpdateTaskRequest,
  TaskType,
  TaskStatus,
  TaskPriority
} from '@/types/taskTypes';
import {
  GetAccessTokenSilentlyType,
  GetAccessTokenWithPopupType
} from '@/types/transcriptionSessionTypes';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const TASKS_ENDPOINT = `${API_BASE_URL}/tasks`;

// Feature flag to switch between mock and real API
const USE_REAL_API = true; // Now using real API

// Mock data for development - replace with real API calls when backend is ready
const mockTasks: Task[] = [
  {
    id: '1',
    title: 'Review John Doe intake session',
    description: 'Review the completed intake session and approve for processing',
    type: TaskType.Approval,
    status: TaskStatus.Pending,
    priority: TaskPriority.High,
    assignedTo: {
      id: 'user1',
      name: 'Sarah Johnson',
      email: 'sarah@company.com'
    },
    assignedBy: {
      id: 'system',
      name: 'System',
    },
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    updatedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), // 1 hour ago
    dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Due in 24 hours
    relatedRecord: {
      id: 'session-123',
      type: 'session',
      title: 'John Doe - Prisoner Intake',
      url: '/records/session-123'
    },
    sessionId: 'session-123',
    approvalStatus: 'pending',
    tags: ['intake', 'high-priority']
  },
  {
    id: '2',
    title: 'Follow up with Jane Smith',
    description: 'Schedule follow-up meeting to discuss additional documentation needed',
    type: TaskType.FollowUp,
    status: TaskStatus.InProgress,
    priority: TaskPriority.Medium,
    assignedTo: {
      id: 'user2',
      name: 'Mike Chen',
      email: 'mike@company.com'
    },
    assignedBy: {
      id: 'user1',
      name: 'Sarah Johnson',
    },
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), // 5 hours ago
    updatedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
    dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(), // Due in 3 days
    relatedRecord: {
      id: 'person-456',
      type: 'person',
      title: 'Jane Smith',
      url: '/people/person-456'
    },
    personId: 'person-456',
    tags: ['follow-up', 'documentation']
  },
  {
    id: '3',
    title: 'Update intake template',
    description: 'Add new questions for medical history section',
    type: TaskType.Review,
    status: TaskStatus.Completed,
    priority: TaskPriority.Low,
    assignedTo: {
      id: 'user3',
      name: 'Alex Rodriguez',
      email: 'alex@company.com'
    },
    assignedBy: {
      id: 'user1',
      name: 'Sarah Johnson',
    },
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
    completedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
    relatedRecord: {
      id: 'template-789',
      type: 'template',
      title: 'Standard Intake Template',
      url: '/templates/template-789'
    },
    templateId: 'template-789',
    tags: ['template', 'medical']
  },
  {
    id: '4',
    title: 'Share compliance report',
    description: 'Share monthly compliance report with legal team',
    type: TaskType.Information,
    status: TaskStatus.Overdue,
    priority: TaskPriority.Critical,
    assignedTo: {
      id: 'user4',
      name: 'Lisa Park',
      email: 'lisa@company.com'
    },
    assignedBy: {
      id: 'user1',
      name: 'Sarah Johnson',
    },
    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), // 10 days ago
    updatedAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(), // 8 days ago
    dueDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // Was due 2 days ago
    tags: ['compliance', 'legal', 'monthly']
  }
];

/**
 * Helper function to acquire Auth0 access token.
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn('Silent token acquisition failed for TaskService:', error);
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      console.log('Falling back to interactive token acquisition for TaskService...');
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error('Failed to acquire access token for TaskService');
      }
      return token;
    }
    throw new Error(`Token acquisition failed: ${error.message}`);
  }
};

/**
 * Helper function to make authenticated API requests.
 */
const makeAuthenticatedRequest = async (
  url: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  options: RequestInit = {}
): Promise<Response> => {
  const token = await getAuthToken(getAccessTokenSilently, getAccessTokenWithPopup);

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers
    }
  });

  if (!response.ok) {
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }

  return response;
};

export class TaskService {
  /**
   * Get all tasks with optional filtering and grouping
   */
  static async getTasks(
    filters?: TaskFilters,
    grouping?: TaskGrouping,
    page = 1,
    pageSize = 50,
    getAccessTokenSilently?: GetAccessTokenSilentlyType,
    getAccessTokenWithPopup?: GetAccessTokenWithPopupType
  ): Promise<TaskListResponse> {
    if (USE_REAL_API && getAccessTokenSilently && getAccessTokenWithPopup) {
      try {
        // Build query parameters
        const params = new URLSearchParams();
        if (filters?.status?.length) params.append('status', filters.status.join(','));
        if (filters?.type?.length) params.append('type', filters.type.join(','));
        if (filters?.priority?.length) params.append('priority', filters.priority.join(','));
        if (filters?.assignedTo?.length) params.append('assignedTo', filters.assignedTo.join(','));
        if (filters?.tags?.length) params.append('tags', filters.tags.join(','));
        if (filters?.dueDate?.from) params.append('dueDateFrom', filters.dueDate.from);
        if (filters?.dueDate?.to) params.append('dueDateTo', filters.dueDate.to);
        if (filters?.search) params.append('search', filters.search);
        if (grouping?.field) params.append('sortBy', grouping.field);
        params.append('sortDescending', (grouping?.order === 'desc').toString());
        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());

        const url = `${TASKS_ENDPOINT}?${params.toString()}`;
        const response = await makeAuthenticatedRequest(
          url,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        const data = await response.json();
        return {
          tasks: data.tasks || [],
          totalCount: data.totalCount || 0,
          hasMore: data.hasMore || false
        };
      } catch (error) {
        console.error('Failed to fetch tasks from API:', error);
        // Fall through to mock data
      }
    }

    // Fall back to mock data
    await new Promise(resolve => setTimeout(resolve, 500));
    let filteredTasks = [...mockTasks];
      
      // Apply filters
      if (filters) {
      if (filters.status?.length) {
        filteredTasks = filteredTasks.filter(task => filters.status!.includes(task.status));
      }
      
      if (filters.type?.length) {
        filteredTasks = filteredTasks.filter(task => filters.type!.includes(task.type));
      }
      
      if (filters.priority?.length) {
        filteredTasks = filteredTasks.filter(task => filters.priority!.includes(task.priority));
      }
      
      if (filters.assignedTo?.length) {
        filteredTasks = filteredTasks.filter(task => 
          task.assignedTo && filters.assignedTo!.includes(task.assignedTo.id)
        );
      }
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredTasks = filteredTasks.filter(task =>
          task.title.toLowerCase().includes(searchLower) ||
          task.description?.toLowerCase().includes(searchLower) ||
          task.tags?.some(tag => tag.toLowerCase().includes(searchLower))
        );
      }
      
      if (filters.tags?.length) {
        filteredTasks = filteredTasks.filter(task =>
          task.tags?.some(tag => filters.tags!.includes(tag))
        );
      }
      
      // Date range filtering
      if (filters.dueDate?.from || filters.dueDate?.to) {
        filteredTasks = filteredTasks.filter(task => {
          if (!task.dueDate) return false;
          const dueDate = new Date(task.dueDate);
          
          if (filters.dueDate?.from && dueDate < new Date(filters.dueDate.from)) {
            return false;
          }
          
          if (filters.dueDate?.to && dueDate > new Date(filters.dueDate.to)) {
            return false;
          }
          
          return true;
        });
      }
    }
    
    // Apply grouping/sorting
    if (grouping) {
      filteredTasks.sort((a, b) => {
        let aValue: any, bValue: any;
        
        switch (grouping.field) {
          case 'status':
            aValue = a.status;
            bValue = b.status;
            break;
          case 'assignee':
            aValue = a.assignedTo?.name || '';
            bValue = b.assignedTo?.name || '';
            break;
          case 'dueDate':
            aValue = a.dueDate ? new Date(a.dueDate) : new Date('9999-12-31');
            bValue = b.dueDate ? new Date(b.dueDate) : new Date('9999-12-31');
            break;
          case 'priority':
            const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
            aValue = priorityOrder[a.priority as keyof typeof priorityOrder];
            bValue = priorityOrder[b.priority as keyof typeof priorityOrder];
            break;
          case 'type':
            aValue = a.type;
            bValue = b.type;
            break;
          case 'createdDate':
            aValue = new Date(a.createdAt);
            bValue = new Date(b.createdAt);
            break;
          default:
            aValue = a.createdAt;
            bValue = b.createdAt;
        }
        
        if (aValue < bValue) return grouping.order === 'asc' ? -1 : 1;
        if (aValue > bValue) return grouping.order === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default sort by created date (newest first)
      filteredTasks.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
    
    // Apply pagination
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedTasks = filteredTasks.slice(startIndex, endIndex);
    
    return {
      tasks: paginatedTasks,
      totalCount: filteredTasks.length,
      hasMore: endIndex < filteredTasks.length
    };
  }
  
  /**
   * Get a single task by ID
   */
  static async getTask(id: string): Promise<Task | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockTasks.find(task => task.id === id) || null;
  }
  
  /**
   * Create a new task
   */
  static async createTask(
    request: CreateTaskRequest,
    getAccessTokenSilently?: GetAccessTokenSilentlyType,
    getAccessTokenWithPopup?: GetAccessTokenWithPopupType
  ): Promise<Task> {
    if (USE_REAL_API && getAccessTokenSilently && getAccessTokenWithPopup) {
      try {
        const response = await makeAuthenticatedRequest(
          TASKS_ENDPOINT,
          getAccessTokenSilently,
          getAccessTokenWithPopup,
          {
            method: 'POST',
            body: JSON.stringify(request)
          }
        );

        const data = await response.json();
        
        // Trigger refresh event
        window.dispatchEvent(new CustomEvent('taskCreated', {
          detail: { task: data }
        }));
        
        return data;
      } catch (error) {
        console.error('Failed to create task via API:', error);
        // Fall through to mock
      }
    }

    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newTask: Task = {
      id: `task-${Date.now()}`,
      title: request.title,
      description: request.description,
      type: request.type,
      status: TaskStatus.Pending,
      priority: request.priority || TaskPriority.Medium,
      assignedTo: request.assignedToId ? {
        id: request.assignedToId,
        name: 'Assigned User', // TODO: Get from user service
        email: 'user@company.com'
      } : undefined,
      assignedBy: {
        id: 'current-user',
        name: 'Current User', // TODO: Get from auth context
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      dueDate: request.dueDate,
      relatedRecord: request.relatedRecord,
      sessionId: request.sessionId,
      templateId: request.templateId,
      personId: request.personId,
      tags: request.tags || []
    };
    
    mockTasks.unshift(newTask);
    
    // Trigger refresh event
    window.dispatchEvent(new CustomEvent('taskCreated', {
      detail: { task: newTask }
    }));
    
    return newTask;
  }
  
  /**
   * Update an existing task
   */
  static async updateTask(request: UpdateTaskRequest): Promise<Task> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const taskIndex = mockTasks.findIndex(task => task.id === request.id);
    if (taskIndex === -1) {
      throw new Error('Task not found');
    }
    
    const updatedTask: Task = {
      ...mockTasks[taskIndex],
      ...request,
      updatedAt: new Date().toISOString(),
      completedAt: request.status === TaskStatus.Completed && !mockTasks[taskIndex].completedAt
        ? new Date().toISOString()
        : mockTasks[taskIndex].completedAt
    };
    
    mockTasks[taskIndex] = updatedTask;
    
    // Trigger refresh event
    window.dispatchEvent(new CustomEvent('taskUpdated', {
      detail: { task: updatedTask }
    }));
    
    return updatedTask;
  }
  
  /**
   * Delete a task
   */
  static async deleteTask(id: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const taskIndex = mockTasks.findIndex(task => task.id === id);
    if (taskIndex === -1) {
      throw new Error('Task not found');
    }
    
    mockTasks.splice(taskIndex, 1);
    
    // Trigger refresh event
    window.dispatchEvent(new CustomEvent('taskDeleted', {
      detail: { taskId: id }
    }));
  }
  
  /**
   * Get tasks requiring approval
   */
  static async getApprovalTasks(): Promise<Task[]> {
    const result = await this.getTasks({
      type: [TaskType.Approval],
      status: [TaskStatus.Pending]
    });
    return result.tasks;
  }
  
  /**
   * Get overdue tasks
   */
  static async getOverdueTasks(): Promise<Task[]> {
    const result = await this.getTasks({
      status: [TaskStatus.Overdue]
    });
    return result.tasks;
  }
  
  /**
   * Get tasks assigned to current user
   */
  static async getMyTasks(userId: string): Promise<Task[]> {
    const result = await this.getTasks({
      assignedTo: [userId]
    });
    return result.tasks;
  }
}
